import type {
  GetShearAnchorStoreySelection200,
  ShearAnchorPosition,
  ShearConnectionCategory,
  ShearConnectionPrioritizationDTO,
} from '@api/fastAPI.schemas'
import { SHEAR_ANCHOR_POSITION_LABEL, SHEAR_ANCHOR_POSITIONS } from './constants'

/**
 * Pure logic around the per-storey shear anchor selection (the exclusions the
 * results view stores). Shared between the results panel and the project
 * system manager, which predicts the backend's self-repair to warn about it.
 */

export type ShearAnchorStoreySelection = GetShearAnchorStoreySelection200
export type ShearPlacement = 'inner' | 'outer'

export const SHEAR_PLACEMENT_LABEL: Record<ShearPlacement, string> = {
  inner: 'Innenwände',
  outer: 'Außenwände',
}

/** The exclusion list of one placement and anchor position, as the API keys it. */
export type ShearExclusionKey = `${ShearPlacement}_${ShearAnchorPosition}`

export const shearExclusionKey = (
  placement: ShearPlacement,
  position: ShearAnchorPosition,
): ShearExclusionKey => `${placement}_${position}`

/** Every placement/position pair, in the order the panel lists them. */
export const SHEAR_PLACEMENTS_AND_POSITIONS: {
  placement: ShearPlacement
  position: ShearAnchorPosition
}[] = (['inner', 'outer'] as ShearPlacement[]).flatMap(placement =>
  SHEAR_ANCHOR_POSITIONS.map(position => ({ placement, position })),
)

/** Label of a storey in the shear anchor sense: the topmost storey is the Dachgeschoss. */
export const shearStoreyLabel = (storey: number, topStorey: number): string => {
  if (storey <= 0) return 'Erdgeschoss'
  if (storey >= topStorey) return 'Dachgeschoss'
  return `${storey}. Obergeschoss`
}

/** The prioritization list a storey's walls of one placement draw from (mirrors the backend). */
export const shearCategoryForStorey = (
  storey: number,
  topStorey: number,
  placement: ShearPlacement,
  position: ShearAnchorPosition,
): ShearConnectionCategory => {
  const group = storey <= 0 ? 'ground_floor' : storey >= topStorey ? 'roof_floor' : 'upper_floor'
  return `${group}_${placement}_${position}` as ShearConnectionCategory
}

/**
 * Whether an anchor is in use for a storey, placement and anchor position —
 * everything not excluded is.
 */
export const isShearAnchorActive = (
  selection: ShearAnchorStoreySelection,
  storeyKey: string,
  placement: ShearPlacement,
  position: ShearAnchorPosition,
  anchorGuid: string,
): boolean =>
  !(selection[storeyKey]?.[shearExclusionKey(placement, position)] ?? []).includes(anchorGuid)

/**
 * Storey lists whose anchors are all excluded under the given prioritization.
 *
 * On a save this state is refused; when a manager edit gets there indirectly,
 * the backend re-activates the list's first anchor — so evaluated against a
 * prioritization *about to be saved*, these are the lists that repair will
 * touch, and the manager warns with exactly these labels.
 */
export const listsWithoutActiveAnchor = (
  selection: ShearAnchorStoreySelection,
  prioritization: ShearConnectionPrioritizationDTO,
  storeys: number[],
): string[] => {
  const topStorey = storeys.length > 0 ? Math.max(...storeys) : 0
  const labels: string[] = []
  for (const storey of storeys) {
    for (const { placement, position } of SHEAR_PLACEMENTS_AND_POSITIONS) {
      const orderedGuids =
        prioritization[shearCategoryForStorey(storey, topStorey, placement, position)] ?? []
      if (
        orderedGuids.length > 0 &&
        orderedGuids.every(
          guid => !isShearAnchorActive(selection, String(storey), placement, position, guid),
        )
      ) {
        labels.push(shearListLabel(storey, topStorey, placement, position))
      }
    }
  }
  return labels
}

/** The label of one storey list, e.g. "Erdgeschoss Innenwände Wandfuß". */
export const shearListLabel = (
  storey: number,
  topStorey: number,
  placement: ShearPlacement,
  position: ShearAnchorPosition,
): string =>
  `${shearStoreyLabel(storey, topStorey)} ${SHEAR_PLACEMENT_LABEL[placement]} ${
    SHEAR_ANCHOR_POSITION_LABEL[position]
  }`

/** The prioritization as it will look once the backend removed a deleted anchor from every list. */
export const prioritizationWithoutAnchor = (
  prioritization: ShearConnectionPrioritizationDTO,
  anchorGuid: string,
): ShearConnectionPrioritizationDTO =>
  Object.fromEntries(
    Object.entries(prioritization).map(([category, guids]: [string, string[] | undefined]) => [
      category,
      (guids ?? []).filter(guid => guid !== anchorGuid),
    ]),
  ) as ShearConnectionPrioritizationDTO

/**
 * Snackbar text for the lists whose first anchor the backend re-activated,
 * because the edit left them without any active one.
 */
export const reactivatedAnchorWarningMessage = (labels: string[]): string =>
  `Kein aktiver Schubanker mehr für: ${labels.join(
    ', ',
  )} — der erste Schubanker der Priorisierung wurde wieder aktiviert`
